import { defineStore } from "pinia";
import { ref } from "vue";
import type {
	Basket,
	BasketItem,
	LookupBaskets
} from "@nwt/norce-pwa/dist/runtime/types/api/shopping/basket";
import type { PaymentMethod } from "@nwt/norce-pwa/dist/runtime/types/api/shopping/paymentMethods";
import { filterBasketItemsForDisplay } from "~/utils/cart/filterBasketItemsForDisplay";
import { getPackContents, type PackContents } from "~/utils/cart/packContents";
import {
	getPackParametricCode,
	isManagedStructurePackEnabled
} from "~/utils/product/packChooseCount";
import { isShippingItem } from "~/utils/cart/isShippingItem";
import {
	basketLineKey,
	isSameBasketLine,
	lineConfiguration
} from "~/layers/base/utils/cart/configuredLine";
import {
	isDepositEnabled,
	isDepositItem,
	type DepositConfig
} from "~/utils/cart/deposit";
import { getDefaultPriceListIds } from "~/utils/pricelist/getDefaultPriceListIds";
import {
	isStaleCheckoutBasket,
	usesSveaCheckout
} from "~/layers/base/utils/cart/staleSveaBasket";
import type { Locale } from "~/types/locale";

/**
 * A multi-pack item and the deposit child that rides with it. Mirrors
 * `MultiPackLine` from `useMultiPack()`, declared here so the store does not
 * import a composable.
 */
export interface MultiPackCartLine {
	item: BasketItem;
	deposit?: { PartNo: string; Quantity: number };
}

// Augment pinia types to include persist option from pinia-plugin-persistedstate
declare module "pinia" {
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	export interface DefineStoreOptionsBase<S extends StateTree, Store> {
		persist?: unknown;
	}
}

/**
 * Result of `cartStore.initCart()`. Carries enough information for the
 * caller (a composable / component with active Nuxt context) to decide
 * what to do next — redirect on a completed order, stash the basket for
 * a success page, etc. The store itself never touches routing.
 */
export type InitCartResult =
	| { kind: "empty" }
	| { kind: "ready"; basket: Basket }
	| { kind: "completed"; basket: Basket; orderReference?: string };

/**
 * In-flight `initCart` / `initCartUser` runs, so overlapping callers share one
 * request instead of each firing its own `get-basket`.
 *
 * Several places legitimately ask for the cart on the same tick: the layout on
 * mount, and `Header`'s watchers on `cartId` and `updated`. `initCart` assigns
 * `this.cartId` before its first await, which trips the `cartId` watcher while
 * the first run is still waiting on the network — so every page load fetched
 * the basket at least twice.
 *
 * Deliberately module-scoped and client-only: on the server this would be
 * shared between concurrent requests, i.e. one visitor could receive another's
 * basket. SSR needs no guard anyway — `cartIds` lives in localStorage, so
 * `initCart` returns `{ kind: "empty" }` there without a request.
 */
/**
 * The key a guest's basket is remembered under, between page loads.
 *
 * A basket belongs to a Norce **application**, so that is what scopes it. The
 * culture used to, and two locales can share one: breadandboxers serves `us`
 * and `global` both as `en-US`, on applications 1566 (USD) and 1570 (EUR).
 * They shared a single slot, so whichever store was visited last overwrote the
 * other's basket id — and asking Norce for a basket that belongs to a different
 * application answers 204, which took the basket out from under an open
 * checkout.
 *
 * The rest of the codebase already scopes by application: a session token is
 * rejected when it was minted in another one (`getApplicationId`), and a signed
 * in shopper's basket is looked up with `ApplicationId eq …`. Only the guest
 * key was still keyed by culture.
 *
 * Read from what Norce actually served rather than from the locale config, so
 * the two cannot drift. Available from the same moment the culture was, so
 * nothing moves in the startup order.
 *
 * No migration: ids stored under the old culture keys are simply not found, and
 * a guest gets a new basket. The one culture where migrating would matter,
 * `en-US`, is also the one where it cannot be done — there is no way to tell
 * which of the two applications the stored id belongs to, which is the bug
 * itself. A signed in shopper loses nothing either way; their basket is found
 * by customer and application.
 */
const cartScopeKey = () => useNorceStore().applicationNorce?.Id?.toString();

let cartInitInFlight: Promise<InitCartResult> | null = null;
let cartUserInitInFlight: Promise<InitCartResult> | null = null;

const shareInFlight = (
	run: () => Promise<InitCartResult>,
	get: () => Promise<InitCartResult> | null,
	set: (value: Promise<InitCartResult> | null) => void
): Promise<InitCartResult> => {
	if (!import.meta.client) return run();

	const pending = get();
	if (pending) return pending;

	const started = run();
	set(started);
	started.finally(() => set(null));

	return started;
};

// Checked once per page load, on first look: a basket reaching checkout now can't turn stale mid-checkout.
const staleCheckedBasketIds = new Set<number>();

interface SveaCheckoutStart {
	basketId: number;
	at: number;
}

/** [GF-94] A basket whose Svea checkout order has likely been cancelled. */
const isStaleSveaCheckoutBasket = async (
	basket: Basket | undefined,
	started: SveaCheckoutStart | undefined,
	created?: string
): Promise<boolean> => {
	if (!basket?.Id || staleCheckedBasketIds.has(basket.Id)) return false;
	staleCheckedBasketIds.add(basket.Id);
	if (!basket.OrderReference) return false;

	const norce = useRuntimeConfig().public.norce;
	const locale = ((norce?.locales ?? []) as Locale[]).find(
		(entry) => entry.code === useNorceStore().localeCode
	);
	if (!usesSveaCheckout(locale, norce?.paymentAdapter)) return false;

	if (started?.basketId === basket.Id) {
		return isStaleCheckoutBasket(started.at, basket.OrderReference);
	}
	// No start recorded in this browser (older basket, other device): fall back to its creation date.
	if (!created) {
		const lookup: LookupBaskets | undefined = await useNorcePwa()
			.lookupBasket({
				$filter: `Id eq ${basket.Id}`,
				...useNorceStore().storeContext
			})
			.catch(() => undefined);
		created = lookup?.value?.[0]?.Created;
	}
	return isStaleCheckoutBasket(
		created ? Date.parse(created) : undefined,
		basket.OrderReference
	);
};

interface State {
	cartId: number | undefined;
	cartIds: Record<string, number | undefined>;
	basket: Ref<Basket | undefined>;
	lastOrderedBasket: Basket | undefined;
	updated: number;
	updating: boolean;
	paymentMethod: PaymentMethod | null | { error: string };
	lastAdded: BasketItem | null;
	/** Structure lines per pack line — see `utils/cart/packContents.ts`. */
	packContents: PackContents;
	/** [GF-94] When this browser first started a Svea checkout, per cart scope. */
	sveaCheckoutStarted: Record<string, SveaCheckoutStart | undefined>;
	/** [GF-94] Signed-in baskets dropped as stale, skipped in the customer's basket lookup. */
	droppedBasketIds: number[];
}

export const useCartStore = defineStore("cartStore", {
	state: (): State => ({
		cartId: undefined,
		cartIds: {},
		basket: ref(),
		lastOrderedBasket: undefined,
		updated: 0,
		updating: false,
		paymentMethod: null,
		lastAdded: null,
		packContents: {},
		sveaCheckoutStarted: {},
		droppedBasketIds: []
	}),
	actions: {
		setCartId(id: number | undefined) {
			this.cartId = id;
			const cartKey = cartScopeKey();
			if (cartKey) this.cartIds[cartKey] = id;
		},
		/** [GF-94] Keeps the first start per basket: a later payment on it may reuse the same Svea order. */
		markSveaCheckoutStarted(basketId: number) {
			const cartKey = cartScopeKey();
			if (!cartKey || this.sveaCheckoutStarted[cartKey]?.basketId === basketId)
				return;
			this.sveaCheckoutStarted[cartKey] = { basketId, at: Date.now() };
		},
		/**
		 * The filtered view: strips shipping and attached child lines and, while
		 * configurable packs are on, remembers a pack's structure lines. Every
		 * add/update path uses it, so what the cart renders is one shape.
		 */
		setDisplayBasket(basket: Basket | null | undefined) {
			useNorceStore().adoptSalesAreaFromBasket(basket?.SalesAreaId);

			const depositConfig = getDepositConfig();
			this.basket = filterBasketItemsForDisplay(
				basket ?? undefined,
				depositConfig
			);
			// Only while configurable packs are on, so a deployment without them
			// keeps an empty map and the cart behaves exactly as before.
			this.packContents = isManagedStructurePackEnabled(getPackParametricCode())
				? getPackContents(basket?.Items, depositConfig)
				: {};

			warnOnUnreadableChildren(basket, this.packContents, depositConfig);
		},
		/**
		 * The write behind `setBasket` and `updateBasket` — the promo code and
		 * the breadandboxers checkout. Both stored the basket unfiltered before
		 * configurable packs, and keep doing so unless the feature is on, so a
		 * deployment without it is untouched.
		 */
		storeBasket(basket: Basket | undefined) {
			if (isManagedStructurePackEnabled(getPackParametricCode())) {
				this.setDisplayBasket(basket);
				return;
			}
			useNorceStore().adoptSalesAreaFromBasket(basket?.SalesAreaId);
			this.basket = basket;
			this.packContents = {};
		},
		/** `storeBasket` plus the end-of-order cleanup. */
		setBasket(basket: Basket | undefined) {
			this.storeBasket(basket);
			if (basket === undefined) {
				this.lastOrderedBasket = undefined;
			}
		},
		/**
		 * Deduped entry point — see `shareInFlight`. `dropStale` forgets a stale
		 * Svea checkout basket ([GF-94]); off on the success page, which waits for it.
		 */
		initCart(dropStale = false): Promise<InitCartResult> {
			return shareInFlight(
				() => this.loadCart(dropStale),
				() => cartInitInFlight,
				(value) => (cartInitInFlight = value)
			);
		},
		async loadCart(dropStale = false): Promise<InitCartResult> {
			const cartKey = cartScopeKey();

			if (cartKey) this.cartId = this.cartIds[cartKey];

			if (!this.cartId || !cartKey) {
				// No cart for this application — clear one left from another.
				this.setDisplayBasket(undefined);
				this.lastOrderedBasket = undefined;
				return { kind: "empty" };
			}

			const basket = await useNorcePwa().getBasket({
				id: +this.cartId,
				...useNorceStore().storeContext
			});

			// Basket was ordered and completed. We don't redirect or stash the
			// `lastOrderedBasket` here — both are routing-aware decisions that
			// belong in `useCartInit`, where Nuxt context is alive. The caller
			// uses `kind: "completed"` + `orderReference` to decide.
			// StatusId 8 = payment completed, order being placed (transition to 10).
			// StatusId 10 = order fully placed. Both mean the cart is done.
			if (basket?.StatusId === 10 || basket?.StatusId === 8) {
				this.cartId = undefined;
				this.cartIds[cartKey] = undefined;
				this.setDisplayBasket(undefined);
				this.lastOrderedBasket = undefined;
				return {
					kind: "completed",
					basket,
					orderReference: basket.OrderReference ?? undefined
				};
			}

			// Forgotten, not deleted: if its Svea order is still alive, a payment
			// finishing in another tab must still find the basket in Norce.
			if (
				dropStale &&
				(await isStaleSveaCheckoutBasket(
					basket,
					this.sveaCheckoutStarted[cartKey]
				))
			) {
				this.cartId = undefined;
				this.cartIds[cartKey] = undefined;
				this.sveaCheckoutStarted[cartKey] = undefined;
				this.setDisplayBasket(undefined);
				this.lastOrderedBasket = undefined;
				return { kind: "empty" };
			}

			// Trust the basket as the source of truth for the active company —
			// the persisted companyId in localStorage may be missing or stale.
			useSelectedCompanyStore().syncFromBasket(basket, userStore().getUser);

			// No `updatePayment` here on purpose. Attaching a payment method is a
			// checkout concern — `Checkout.vue` does it itself when the basket
			// reaches checkout without one. Doing it while the shopper merely
			// browses cost three extra requests (get-payment-method,
			// update-basket-payment-method and a second get-basket to pick the
			// method up) on the first page load after a basket was created outside
			// `createNewBasket`.
			this.lastOrderedBasket = undefined;
			this.setDisplayBasket(basket);
			return { kind: "ready", basket };
		},
		/** Deduped entry point — see `shareInFlight` and `initCart`. */
		initCartUser(dropStale = false): Promise<InitCartResult> {
			return shareInFlight(
				() => this.loadCartUser(dropStale),
				() => cartUserInitInFlight,
				(value) => (cartUserInitInFlight = value)
			);
		},
		async loadCartUser(dropStale = false): Promise<InitCartResult> {
			const cartKey = cartScopeKey();
			if (cartKey) {
				this.cartId = this.cartIds[cartKey];
			}

			// Before doing the active-only lookup, check if the stored basket is
			// already a completed order. initCartUser's lookupBasket filter only
			// matches StatusId eq 3, so it would silently miss a completed basket
			// and create a new one — losing the reference the success page needs.
			if (this.cartId) {
				const stored = await useNorcePwa().getBasket({
					id: +this.cartId,
					...useNorceStore().storeContext
				});
				if (stored?.StatusId === 10 || stored?.StatusId === 8) {
					this.cartId = undefined;
					if (cartKey) this.cartIds[cartKey] = undefined;
					this.setDisplayBasket(undefined);
					this.lastOrderedBasket = undefined;
					return {
						kind: "completed",
						basket: stored,
						orderReference: stored.OrderReference ?? undefined
					};
				}
			}

			const applicationId = useNorceStore().applicationNorce?.Id;
			const activeBasketFilter = [
				`BuyerCustomerId eq ${userStore()?.getUser?.Id}`,
				"StatusId eq 3",
				...(applicationId ? [`ApplicationId eq ${applicationId}`] : [])
			].join(" and ");
			const anonymousBasketFilter = [
				`Id eq ${this.cartId}`,
				"StatusId eq 3",
				...(applicationId ? [`ApplicationId eq ${applicationId}`] : [])
			].join(" and ");
			// check for basket by user and with status Active
			const basketResult: LookupBaskets = await useNorcePwa().lookupBasket({
				$filter: activeBasketFilter,
				...useNorceStore().storeContext
			});
			// [GF-94] A basket dropped as stale is still active in Norce; never pick it again.
			const activeBaskets = basketResult.value.filter(
				({ Id }) => !this.droppedBasketIds.includes(Id)
			);
			let basketFound;

			if (this.cartId) {
				basketFound = activeBaskets.filter(({ Id }) => Id === +this.cartId!)[0];
			}
			if (!basketFound) {
				basketFound = activeBaskets[0];
			}
			if (activeBaskets.length > 0 && basketFound?.Id) {
				const basketId = basketFound.Id;
				const userBasket: Basket = await useNorcePwa().getBasket({
					id: basketId,
					...useNorceStore().storeContext
				});

				// Left in Norce like a guest's, so this browser remembers to skip it.
				if (
					dropStale &&
					(await isStaleSveaCheckoutBasket(
						userBasket,
						this.sveaCheckoutStarted[cartScopeKey() ?? ""],
						basketFound.Created
					))
				) {
					this.droppedBasketIds = [...this.droppedBasketIds, basketId].slice(
						-20
					);
					await this.createNewBasket();
					return { kind: "ready", basket: this.basket! };
				}

				await this.updateBasketFromGuest(userBasket, basketId);

				const freshBasket = await useNorcePwa().getBasket({
					id: basketId,
					...useNorceStore().storeContext
				});

				// Trust the basket as the source of truth for the active company.
				useSelectedCompanyStore().syncFromBasket(
					freshBasket,
					userStore().getUser
				);

				this.setDisplayBasket(freshBasket);
				this.cartId = basketId;
				const cartKey = cartScopeKey();
				if (cartKey) this.cartIds[cartKey] = basketId;
			} else {
				if (this.cartId) {
					const basketResult = await useNorcePwa().lookupBasket({
						$filter: anonymousBasketFilter,
						...useNorceStore().storeContext
					});
					const basketCreatedAnonymous = basketResult.value.filter(
						({ Id }) => Id === this.cartId
					)[0];
					if (basketCreatedAnonymous) {
						const user = userStore().getUser;
						if (user) {
							await useNorcePwa().updateBuyer(
								{
									basketId: this.cartId,
									updatedBy: user.Id!,
									...useNorceStore().storeContext
								},
								{ ...user }
							);
						}
						const basket = await useNorcePwa().getBasket({
							id: this.cartId,
							...useNorceStore().storeContext
						});

						// Trust the basket as the source of truth for the active company.
						useSelectedCompanyStore().syncFromBasket(
							basket,
							userStore().getUser
						);

						this.setDisplayBasket(basket);
					} else {
						await this.createNewBasket();
					}
				} else {
					// create new basket for user
					await this.createNewBasket();
				}
			}
			return { kind: "ready", basket: this.basket! };
		},
		itemsCount() {
			const depositConfig = getDepositConfig();
			// The badge counts what the cart renders. With configurable packs on
			// the rows also hide a pack's child lines, so the badge applies the
			// same filter; with the feature off it keeps the rule it has always
			// used. Components read `useDisplayBasket()`, which is gated the
			// same way — the store cannot use a composable that reads it.
			const items = isManagedStructurePackEnabled(getPackParametricCode())
				? filterBasketItemsForDisplay(
						this.basket,
						depositConfig
					)?.Items?.filter((item) => !isDepositItem(item, depositConfig))
				: this.basket?.Items?.filter(
						(item) =>
							!isShippingItem(item) && !isDepositItem(item, depositConfig)
					);
			return items?.reduce((acc, item) => acc + item.Quantity, 0) ?? 0;
		},
		/**
		 * `depositChild` is the bottle-deposit line for THIS add (quantity =
		 * item quantity × the resolved deposit units). Rides as a Norce child line
		 * (ParentLineNo), so removing the product removes the deposit too.
		 */
		async addToCart(item: BasketItem, depositChild?: BasketItem) {
			// Deposit fee lines only enter the basket as child lines built by
			// the add-to-cart call sites — never as direct items.
			if (isShippingItem(item) || isDepositItem(item, getDepositConfig())) {
				return;
			}
			this.updating = true;
			const { $toast, $i18n } = useNuxtApp() as unknown as {
				$toast: { success: (message: string) => void };
				$i18n: { t: (key: string, params?: Record<string, unknown>) => string };
			};
			// [GF-71] Off for themes with their own "added to cart" popup.
			const showAddedToast =
				useRuntimeConfig().public.cart?.addedToast !== false;
			const notifyAdded = () => {
				if (!showAddedToast) return;
				$toast.success($i18n.t("product_added_to_cart", { name: item.Name }));
			};
			const userId = userStore()?.getUser?.Id;
			const norcePwa = useNorcePwa();

			if (!this.cartId) {
				const companyId = useSelectedCompanyStore().companyId;
				const pricelistSeed = getPreferredPricelistSeed();
				const basket = await norcePwa.createBasket(
					{
						createdBy: userId?.toString() || "0",
						...useNorceStore().storeContext,
						...(pricelistSeed ? { pricelistSeed } : {})
					},
					companyId ? { CompanyId: companyId } : undefined
				);

				this.cartId = basket.Id;
				const cartKey = cartScopeKey();
				if (cartKey) this.cartIds[cartKey] = basket.Id;
			}

			const itemInCart = this?.basket?.Items?.find((i: BasketItem) =>
				isSameBasketLine(i, item)
			);

			try {
				let updatedBasket = null;

				if (itemInCart) {
					const basketItem = {
						LineNo: itemInCart.LineNo,
						PartNo: item.PartNo,
						Quantity: itemInCart.Quantity + item.Quantity,
						...lineConfiguration(itemInCart)
					};

					updatedBasket = await norcePwa.updateBasketItem(
						{
							basketId: this.cartId,
							...useNorceStore().storeContext
						},
						basketItem
					);
					if (
						depositChild?.PartNo &&
						item.Quantity &&
						itemInCart.LineNo != null
					) {
						updatedBasket = await syncDepositChildQuantity(
							updatedBasket,
							this.cartId!,
							itemInCart.LineNo,
							basketItem.Quantity,
							depositChild.Quantity / item.Quantity,
							depositChild.PartNo,
							userId?.toString() || "0"
						);
					}
				} else if (depositChild) {
					updatedBasket = await norcePwa.addToBasketWithChildren(
						{
							basketId: this.cartId,
							createdBy: userId?.toString() || "0",
							...useNorceStore().storeContext
						},
						[item, depositChild]
					);
				} else {
					updatedBasket = await norcePwa.addToBasket(
						{
							basketId: this.cartId,
							createdBy: userId?.toString() || "0",
							...useNorceStore().storeContext
						},
						item
					);
				}
				this.setDisplayBasket(updatedBasket);

				notifyAdded();
			} catch (error) {
				if (!isStaleBasketError(error)) {
					throw error;
				}
				// Basket is stale or invalid — discard it, create a fresh one and retry
				const cartKey = cartScopeKey();
				this.cartId = undefined;
				if (cartKey) this.cartIds[cartKey] = undefined;
				this.setDisplayBasket(undefined);

				const companyId = useSelectedCompanyStore().companyId;
				const freshBasket = await norcePwa.createBasket(
					{
						createdBy: userId?.toString() || "0",
						...useNorceStore().storeContext
					},
					companyId ? { CompanyId: companyId } : undefined
				);
				this.cartId = freshBasket.Id;
				if (cartKey) this.cartIds[cartKey] = freshBasket.Id;

				const updatedBasket = depositChild
					? await norcePwa.addToBasketWithChildren(
							{
								basketId: this.cartId,
								createdBy: userId?.toString() || "0",
								...useNorceStore().storeContext
							},
							[item, depositChild]
						)
					: await norcePwa.addToBasket(
							{
								basketId: this.cartId,
								createdBy: userId?.toString() || "0",
								...useNorceStore().storeContext
							},
							item
						);
				this.setDisplayBasket(updatedBasket);
				notifyAdded();
			} finally {
				this.updating = false;
				this.rememberLastAdded(item);
			}
		},
		/**
		 * Remembers the basket line that was just added, for themes that show an
		 * "added to cart" popup instead of (or next to) the toast. Read from the
		 * basket, not from the item passed in, so the popup gets the line as Norce
		 * returned it — Id, name and price included.
		 *
		 * Always a fresh object: the popup watches `lastAdded` by reference, so
		 * re-adding the same product has to produce a new value or it won't
		 * re-open. Left untouched when the line isn't in the basket, i.e. the add
		 * failed.
		 */
		rememberLastAdded(added: BasketItem) {
			const line = this.basket?.Items?.find((item: BasketItem) =>
				isSameBasketLine(item, added)
			);
			if (!line) {
				console.warn("Added item not found in basket items");
				return;
			}
			this.lastAdded = { ...line };
		},
		dismissLastAdded() {
			this.lastAdded = null;
		},
		/**
		 * Picked contents via `InsertBasketItemWithChildren`; a plain add would
		 * expand the whole structure.
		 *
		 * Always its own line: Norce never merges two inserts, and a parent
		 * quantity change rebuilds the children and loses the picks — which is
		 * also why the cart shows a pack line's quantity as text
		 * (`SharedProductOrderItem`). A fixed bundle goes through `addToCart`.
		 */
		async addManagedStructurePackToCart(items: BasketItem[]) {
			// Deposit fee lines are legitimate children here (built by the
			// add-to-cart call sites), so only shipping items are filtered out.
			items = items.filter((item) => !isShippingItem(item));
			const [parent] = items;
			if (!parent) {
				return;
			}
			this.updating = true;
			const { $toast, $i18n } = useNuxtApp() as unknown as {
				$toast: { success: (message: string) => void };
				$i18n: { t: (key: string, params?: Record<string, unknown>) => string };
			};
			const userId = userStore()?.getUser?.Id;
			const createdBy = userId?.toString() || "0";
			const norcePwa = useNorcePwa();

			const createBasket = async () => {
				const companyId = useSelectedCompanyStore().companyId;
				const pricelistSeed = getPreferredPricelistSeed();
				const basket = await norcePwa.createBasket(
					{
						createdBy,
						...useNorceStore().storeContext,
						...(pricelistSeed ? { pricelistSeed } : {})
					},
					companyId ? { CompanyId: companyId } : undefined
				);
				this.cartId = basket.Id;
				const cartKey = cartScopeKey();
				if (cartKey) this.cartIds[cartKey] = basket.Id;
			};

			const insertPack = async () =>
				this.setDisplayBasket(
					await norcePwa.addToBasketWithChildren(
						{
							basketId: this.cartId!,
							createdBy,
							...useNorceStore().storeContext
						},
						items
					)
				);

			try {
				if (!this.cartId) {
					await createBasket();
				}
				try {
					await insertPack();
				} catch (error) {
					if (!isStaleBasketError(error)) {
						throw error;
					}
					// Basket is stale or invalid — discard it and retry once.
					const cartKey = cartScopeKey();
					this.cartId = undefined;
					if (cartKey) this.cartIds[cartKey] = undefined;
					this.setDisplayBasket(undefined);
					await createBasket();
					await insertPack();
				}
				$toast.success($i18n.t("product_added_to_cart", { name: parent.Name }));
			} finally {
				this.updating = false;
			}
		},
		/**
		 * Multi pack add: each picked item becomes its own basket line at its
		 * own price, with its container deposit as a child. The pack product is
		 * never added, so this is deliberately not
		 * `addManagedStructurePackToCart` — that would show one row instead of
		 * the units the customer chose.
		 *
		 * Up to four Norce calls (update, deposit re-sync, insert, deposit
		 * insert), each committed as it succeeds — see `commit` below.
		 */
		async addMultiPackToCart(lines: MultiPackCartLine[]) {
			const depositConfig = getDepositConfig();
			const wanted = lines.filter(
				({ item }) =>
					item.PartNo &&
					item.Quantity > 0 &&
					!isShippingItem(item) &&
					!isDepositItem(item, depositConfig)
			);
			if (!wanted.length) {
				return;
			}
			this.updating = true;
			const { $toast, $i18n } = useNuxtApp() as unknown as {
				$toast: { success: (message: string) => void };
				$i18n: { t: (key: string, params?: Record<string, unknown>) => string };
			};
			const userId = userStore()?.getUser?.Id;
			const createdBy = userId?.toString() || "0";
			const norcePwa = useNorcePwa();

			const createBasket = async () => {
				const companyId = useSelectedCompanyStore().companyId;
				const pricelistSeed = getPreferredPricelistSeed();
				const basket = await norcePwa.createBasket(
					{
						createdBy,
						...useNorceStore().storeContext,
						...(pricelistSeed ? { pricelistSeed } : {})
					},
					companyId ? { CompanyId: companyId } : undefined
				);
				this.cartId = basket.Id;
				const cartKey = cartScopeKey();
				if (cartKey) this.cartIds[cartKey] = basket.Id;
			};

			/*
				Up to four writes, each committed as it succeeds, so a part-way
				failure leaves the cart showing what Norce holds.

				`written` gates the retry below: it re-inserts everything as new
				lines, which is only true while nothing has gone through — after
				the first write that replaces `line.Quantity + item.Quantity`
				with `item.Quantity` and abandons the rest of the basket.
				`isStaleBasketError` is any 400/404, i.e. also an out-of-stock
				rejection.
			*/
			let written = false;
			const commit = (basket: Basket | null | undefined) => {
				written = true;
				this.setDisplayBasket(basket ?? undefined);
				return basket;
			};

			const insertLines = async () => {
				const basketId = this.cartId!;
				const existingLines = (this.basket?.Items ?? []).filter(
					(item) => !item.ParentLineNo
				);
				const alreadyInBasket = wanted.filter(({ item }) =>
					existingLines.some((line) => isSameBasketLine(line, item))
				);
				const newToBasket = wanted.filter(
					(line) => !alreadyInBasket.includes(line)
				);

				let updatedBasket: Basket | null | undefined = this.basket;

				if (alreadyInBasket.length) {
					updatedBasket = commit(
						await norcePwa.updateBasketItems(
							{ basketId, ...useNorceStore().storeContext },
							alreadyInBasket.map(({ item }) => {
								const line = existingLines.find((existing) =>
									isSameBasketLine(existing, item)
								)!;
								return {
									LineNo: line.LineNo,
									PartNo: item.PartNo,
									Quantity: line.Quantity + item.Quantity
								};
							})
						)
					);
					// Norce leaves child lines alone on a parent quantity change,
					// so each touched line's deposit is brought back in step.
					for (const { item, deposit } of alreadyInBasket) {
						const line = existingLines.find((existing) =>
							isSameBasketLine(existing, item)
						);
						if (!deposit || line?.LineNo == null) continue;
						updatedBasket = commit(
							await syncDepositChildQuantity(
								updatedBasket,
								basketId,
								line.LineNo,
								line.Quantity + item.Quantity,
								deposit.Quantity / item.Quantity,
								deposit.PartNo,
								createdBy
							)
						);
					}
				}

				if (newToBasket.length) {
					updatedBasket = commit(
						await norcePwa.addItemsToBasket(
							{ basketId, createdBy, ...useNorceStore().storeContext },
							newToBasket.map(({ item }) => item)
						)
					);

					// Only the lines just inserted get a deposit child: Norce adds
					// its own zero-price shipping line here, and attaching a
					// deposit to that would charge for a container that has none.
					const depositChildren: BasketItem[] = [];
					for (const { item, deposit } of newToBasket) {
						const inserted = updatedBasket?.Items?.find(
							(line) => !line.ParentLineNo && line.PartNo === item.PartNo
						);
						if (!deposit || inserted?.LineNo == null) continue;
						depositChildren.push({
							...deposit,
							ParentLineNo: inserted.LineNo
						});
					}

					if (depositChildren.length) {
						updatedBasket = commit(
							await norcePwa.addItemsToBasket(
								{ basketId, createdBy, ...useNorceStore().storeContext },
								depositChildren
							)
						);
					}
				}

				commit(updatedBasket);
			};

			try {
				if (!this.cartId) {
					await createBasket();
				}
				try {
					await insertLines();
				} catch (error) {
					if (!isStaleBasketError(error) || written) {
						throw error;
					}
					// Nothing went through and the basket is stale or invalid —
					// discard it, create a fresh one and retry. `insertLines`
					// reads `this.basket`, so clearing it first makes the retry
					// insert everything as new lines.
					const cartKey = cartScopeKey();
					this.cartId = undefined;
					if (cartKey) this.cartIds[cartKey] = undefined;
					this.setDisplayBasket(undefined);
					await createBasket();
					await insertLines();
				}
				$toast.success($i18n.t("Items were added to cart"));
			} finally {
				this.updating = false;
			}
		},
		/**
		 * `options.silent` suppresses the toast — for a call that rides along
		 * with another add that already reported to the customer (the PDP's
		 * Match & Buy add-ons).
		 */
		async addItemsToCart(items: BasketItem[], options?: { silent?: boolean }) {
			const depositConfig = getDepositConfig();
			const itemsToAdd = items.filter(
				(item) => !isShippingItem(item) && !isDepositItem(item, depositConfig)
			);
			if (!itemsToAdd.length) {
				return;
			}
			this.updating = true;
			const { $toast, $i18n } = useNuxtApp() as unknown as {
				$toast: { success: (message: string) => void };
				$i18n: { t: (key: string, params?: Record<string, unknown>) => string };
			};
			const userId = userStore()?.getUser?.Id;
			const norcePwa = useNorcePwa();

			try {
				if (!this.cartId) {
					const companyId = useSelectedCompanyStore().companyId;
					const pricelistSeed = getPreferredPricelistSeed();
					const basket = await norcePwa.createBasket(
						{
							createdBy: userId?.toString() || "0",
							...useNorceStore().storeContext,
							...(pricelistSeed ? { pricelistSeed } : {})
						},
						companyId ? { CompanyId: companyId } : undefined
					);

					this.cartId = basket.Id;
					const cartKey = cartScopeKey();
					if (cartKey) this.cartIds[cartKey] = basket.Id;
				}

				const itemsInBasket: BasketItem[] = [];
				const itemsNotInBasket: BasketItem[] = [];

				itemsToAdd.forEach((item) => {
					const itemInBasket = this?.basket?.Items?.find((i: BasketItem) =>
						isSameBasketLine(i, item)
					);

					if (itemInBasket) {
						itemsInBasket.push({
							...item,
							Quantity: itemInBasket.Quantity + item.Quantity,
							LineNo: itemInBasket.LineNo,
							...lineConfiguration(itemInBasket)
						});
					} else {
						itemsNotInBasket.push(item);
					}
				});

				try {
					if (itemsInBasket.length > 0) {
						const updatedBasket = await norcePwa.updateBasketItems(
							{ basketId: this.cartId, ...useNorceStore().storeContext },
							itemsInBasket
						);
						this.setDisplayBasket(updatedBasket);
					}

					if (itemsNotInBasket.length > 0) {
						const updatedBasket = await norcePwa.addItemsToBasket(
							{
								basketId: this.cartId,
								createdBy: userId?.toString() || "0",
								...useNorceStore().storeContext
							},
							itemsNotInBasket
						);
						this.setDisplayBasket(updatedBasket);
					}

					if (!options?.silent) {
						$toast.success($i18n.t("Items were added to cart"));
					}
				} catch (error) {
					if (!isStaleBasketError(error)) {
						throw error;
					}
					// Basket is stale or invalid — discard it, create a fresh one and retry
					const cartKey = cartScopeKey();
					this.cartId = undefined;
					if (cartKey) this.cartIds[cartKey] = undefined;
					this.setDisplayBasket(undefined);

					const companyId = useSelectedCompanyStore().companyId;
					const freshBasket = await norcePwa.createBasket(
						{
							createdBy: userId?.toString() || "0",
							...useNorceStore().storeContext
						},
						companyId ? { CompanyId: companyId } : undefined
					);
					this.cartId = freshBasket.Id;
					if (cartKey) this.cartIds[cartKey] = freshBasket.Id;

					const updatedBasket = await norcePwa.addItemsToBasket(
						{
							basketId: freshBasket.Id,
							createdBy: userId?.toString() || "0",
							...useNorceStore().storeContext
						},
						itemsToAdd
					);
					this.setDisplayBasket(updatedBasket);
					if (!options?.silent) {
						$toast.success($i18n.t("Items were added to cart"));
					}
				}
			} finally {
				this.updating = false;
			}
		},
		async removeFromCart(lineno: number) {
			const itemToRemove = this.basket?.Items?.find(
				(item) => item.LineNo === lineno
			);
			if (
				isShippingItem(itemToRemove) ||
				isDepositItem(itemToRemove, getDepositConfig())
			) {
				return;
			}
			this.updating = true;
			const userId = userStore()?.getUser?.Id;
			useHelpersStore().setIsLoading(true);
			const updatedBasket = await useNorcePwa().deleteBasketItem({
				basketId: this.cartId,
				lineNo: lineno,
				createdBy: userId?.toString() || "0",
				...useNorceStore().storeContext
			});
			this.setDisplayBasket(updatedBasket);
			this.updating = false;
			useHelpersStore().setIsLoading(false);
		},
		async updateCartItem(lineno: number, partno: string, quantity: number) {
			const depositConfig = getDepositConfig();
			const itemToUpdate = this.basket?.Items?.find(
				(item) => item.LineNo === lineno
			);
			if (
				isShippingItem(itemToUpdate) ||
				isDepositItem(itemToUpdate, depositConfig)
			) {
				return;
			}
			this.updating = true;
			useHelpersStore().setIsLoading(true);
			const oldQuantity = itemToUpdate?.Quantity;
			const basketItem = {
				LineNo: lineno,
				PartNo: partno,
				Quantity: quantity,
				...lineConfiguration(itemToUpdate)
			};

			try {
				let updatedBasket: Basket | null | undefined =
					await useNorcePwa().updateBasketItem(
						{
							basketId: this.cartId,
							...useNorceStore().storeContext
						},
						basketItem
					);
				// Norce keeps child lines untouched on a parent quantity change —
				// rescale the deposit child so the deposit stays per unit.
				const depositChild = updatedBasket?.Items?.find(
					(i) => i.ParentLineNo === lineno && isDepositItem(i, depositConfig)
				);
				if (depositChild?.PartNo && oldQuantity && depositChild.Quantity) {
					updatedBasket = await syncDepositChildQuantity(
						updatedBasket,
						this.cartId!,
						lineno,
						quantity,
						depositChild.Quantity / oldQuantity,
						depositChild.PartNo,
						userStore()?.getUser?.Id?.toString() || "0"
					);
				}
				this.setDisplayBasket(updatedBasket);
			} finally {
				this.updating = false;
				useHelpersStore().setIsLoading(false);
			}
		},
		async updateBasketFromGuest(userBasket: Basket, userBasketId: number) {
			const userId = userStore()?.getUser?.Id;
			if (this.cartId !== userBasketId) {
				const cartIdBeforeMerge = this.cartId;
				if (!cartIdBeforeMerge) {
					return true;
				}
				const guestBasket = await useNorcePwa().getBasket({
					id: cartIdBeforeMerge,
					...useNorceStore().storeContext
				});

				if (guestBasket && guestBasket?.Items?.length) {
					// Deposit child lines never merge as standalone items — the ones
					// already attached to user-basket lines stay with their parents.
					const depositConfig = getDepositConfig();
					const guestItems = guestBasket.Items.filter(
						(item) =>
							!isShippingItem(item) && !isDepositItem(item, depositConfig)
					);
					const userItems =
						userBasket.Items?.filter(
							(item) =>
								!isShippingItem(item) && !isDepositItem(item, depositConfig)
						) ?? [];
					const itemsCombined = combineQuantityOfBasketItems(
						guestItems,
						userItems
					).map((item) => ({ ...item, ...lineConfiguration(item) }));

					const userLineKeys = (userBasket?.Items ?? []).map(basketLineKey);
					const itemsInUserBasket = itemsCombined.filter((item) =>
						userLineKeys.includes(basketLineKey(item))
					);
					const itemsInGuestBasket = itemsCombined.filter(
						(item) => !userLineKeys.includes(basketLineKey(item))
					);

					if (itemsInUserBasket) {
						await useNorcePwa().updateBasketItems(
							{ basketId: userBasketId, ...useNorceStore().storeContext },
							itemsInUserBasket
						);
					}
					await useNorcePwa().addItemsToBasket(
						{
							basketId: userBasketId,
							createdBy: userId?.toString() || "0",
							...useNorceStore().storeContext
						},
						itemsInGuestBasket
					);
					// The merge above only writes parent lines, so deposit
					// children are re-synced to the combined quantities before
					// the guest basket (holding the only copy for guest-only
					// products) is deleted.
					await syncDepositChildrenAfterMerge(
						userBasketId,
						[guestBasket, userBasket],
						userId?.toString() || "0"
					);
					if (cartIdBeforeMerge) {
						return await useNorcePwa().deleteBasket({
							basketId: cartIdBeforeMerge,
							updatedBy: userId?.toString() || "0",
							...useNorceStore().storeContext
						});
					}
				}
			}
			return true;
		},
		async createNewBasket() {
			const userId = userStore()?.getUser?.Id;
			const companyId = useSelectedCompanyStore().companyId;
			const pricelistSeed = getPreferredPricelistSeed();

			// create new basket for user
			const basketItem: Basket = {
				...(userId && { CustomerId: userId }),
				Items: this.basket?.Items ?? [],
				...(companyId ? { CompanyId: companyId } : {})
			};
			let basket;
			basket = await useNorcePwa().createBasket(
				{
					createdBy: userId?.toString() ?? "0",
					...useNorceStore().storeContext,
					...(pricelistSeed ? { pricelistSeed } : {})
				},
				basketItem
			);
			this.cartId = basket.Id;
			const cartKey = cartScopeKey();
			if (cartKey) this.cartIds[cartKey] = basket.Id;
			await this.updatePayment(basket);

			if (!basket?.PaymentMethodId && basket.Id) {
				basket = await useNorcePwa().getBasket({
					id: basket.Id,
					...useNorceStore().storeContext
				});
			}
			this.setDisplayBasket(basket);
			this.cartId = basket.Id;
			if (cartKey) this.cartIds[cartKey] = basket.Id;
		},
		async updatePayment(basket: Basket) {
			if (basket?.PaymentMethodId || this.paymentMethod) return;

			// Resolve composables synchronously — useNuxtApp() can throw if accessed
			// after an `await` in a Pinia action when the Nuxt context isn't restored.
			const norcePwa = useNorcePwa();
			const norceStore = useNorceStore();
			const currencyId = norceStore.getCurrentCurrency?.Id;
			const storeContext = norceStore.storeContext;

			this.paymentMethod = await norcePwa.getPaymentMethod({
				currencyId,
				code: "norcecheckout"
			});

			if (this.paymentMethod) {
				const paymentMethod: PaymentMethod = JSON.parse(
					JSON.stringify(this.paymentMethod)
				);
				await norcePwa.updateBasketPaymentMethod({
					basketId: basket.Id!,
					paymentMethodId: paymentMethod.PaymentMethodId,
					...storeContext
				});
			}
		},
		async updateBasket(basket: Basket) {
			this.updating = true;
			try {
				this.storeBasket(
					await useNorcePwa().updateBasket(useNorceStore().storeContext, basket)
				);
			} finally {
				// finally so a failed write (offline, Norce 500) can't leave `updating`
				// stuck true, which would keep the cart spinner and every add-to-cart
				// button disabled until a reload.
				this.updating = false;
			}
		},
		async clearBasket() {
			if (this.cartId) {
				const userId = userStore()?.getUser?.Id;
				await useNorcePwa().deleteBasket({
					basketId: this.cartId,
					updatedBy: userId?.toString() || "0",
					...useNorceStore().storeContext
				});
				this.setDisplayBasket(undefined);
				this.cartId = undefined;
				const cartKey = cartScopeKey();
				if (cartKey) this.cartIds[cartKey] = undefined;
			}
		}
	},
	persist: [
		{
			storage: persistedState.localStorage,
			pick: ["cartIds", "sveaCheckoutStarted", "droppedBasketIds"]
		}
	]
});

/**
 * `getPackContents` reads structure children by `ChildTypeId === 1` — verified
 * against staging for both add paths — while `filterBasketItemsForDisplay`
 * hides every child line. Should Norce ever return a structure child with
 * another child type, it would be hidden and not listed as contents either, so
 * a dev build says so instead of quietly dropping it.
 */
const warnOnUnreadableChildren = (
	basket: Basket | null | undefined,
	contents: PackContents,
	depositConfig: DepositConfig | undefined
) => {
	if (
		!import.meta.dev ||
		!isManagedStructurePackEnabled(getPackParametricCode())
	) {
		return;
	}

	const lineNos = new Set((basket?.Items ?? []).map((item) => item.LineNo));
	const unreadable = (basket?.Items ?? []).filter(
		(item) =>
			item.ParentLineNo != null &&
			lineNos.has(item.ParentLineNo) &&
			!isDepositItem(item, depositConfig) &&
			!(contents[item.ParentLineNo] ?? []).some(
				(child) => child.LineNo === item.LineNo
			)
	);

	if (unreadable.length) {
		console.warn(
			"[pack] child lines the cart cannot show — neither rows nor pack contents:",
			unreadable.map((item) => `${item.PartNo} (line ${item.LineNo})`)
		);
	}
};

const getDepositConfig = (): DepositConfig | undefined => {
	try {
		return useDepositConfig();
	} catch {
		// Outside a Nuxt context (unit tests) the deposit feature is off.
		return undefined;
	}
};

// Norce keeps child lines untouched when a parent's quantity changes, so the
// bottle-deposit child is brought to parentQuantity × perUnit here. Adds the
// child when it is missing (product previously added without deposit data).
// After a guest→user merge only parent lines were written, so every merged
// parent's deposit child is re-synced to the combined quantity. The per-unit
// count is recovered from the child/parent ratio in the pre-merge baskets
// (guest first — it may hold the only copy for guest-only products).
const syncDepositChildrenAfterMerge = async (
	basketId: number,
	preMergeBaskets: (Basket | undefined | null)[],
	createdBy: string
) => {
	const depositConfig = getDepositConfig();
	if (!isDepositEnabled(depositConfig)) return;

	const metaByProductId = new Map<
		number,
		{ depositPartNo: string; perUnit: number }
	>();
	for (const basket of preMergeBaskets) {
		for (const parent of basket?.Items ?? []) {
			if (parent.ParentLineNo || !parent.ProductId || !parent.Quantity) {
				continue;
			}
			if (metaByProductId.has(parent.ProductId)) continue;
			const child = basket?.Items?.find(
				(item) =>
					item.ParentLineNo === parent.LineNo &&
					isDepositItem(item, depositConfig)
			);
			if (child?.PartNo && child.Quantity) {
				metaByProductId.set(parent.ProductId, {
					depositPartNo: child.PartNo,
					perUnit: child.Quantity / parent.Quantity
				});
			}
		}
	}
	if (!metaByProductId.size) return;

	let merged = await useNorcePwa().getBasket({
		id: basketId,
		...useNorceStore().storeContext
	});
	for (const line of merged?.Items ?? []) {
		if (line.ParentLineNo || !line.ProductId || line.LineNo == null) continue;
		const meta = metaByProductId.get(line.ProductId);
		if (!meta) continue;
		merged =
			(await syncDepositChildQuantity(
				merged,
				basketId,
				line.LineNo,
				line.Quantity,
				meta.perUnit,
				meta.depositPartNo,
				createdBy
			)) ?? merged;
	}
};

const syncDepositChildQuantity = async (
	basket: Basket | null | undefined,
	basketId: number,
	parentLineNo: number,
	parentQuantity: number,
	perUnit: number,
	depositPartNo: string,
	createdBy: string
): Promise<Basket | null | undefined> => {
	const depositConfig = getDepositConfig();
	const child = basket?.Items?.find(
		(item) =>
			item.ParentLineNo === parentLineNo && isDepositItem(item, depositConfig)
	);
	const target = Math.round(parentQuantity * perUnit);

	if (child?.LineNo != null) {
		if (child.Quantity === target) {
			return basket;
		}
		return useNorcePwa().updateBasketItem(
			{ basketId, ...useNorceStore().storeContext },
			{ LineNo: child.LineNo, PartNo: child.PartNo, Quantity: target }
		);
	}
	if (target > 0) {
		return useNorcePwa().addToBasket(
			{ basketId, createdBy, ...useNorceStore().storeContext },
			{
				// No `PriceListId`. The call carries the shopper's `pricelistSeed`,
				// which is what prices the line; what used to be passed here was the
				// parent's first applicable list, which was never the deposit
				// product's own anyway.
				PartNo: depositPartNo,
				Quantity: target,
				ParentLineNo: parentLineNo
			}
		);
	}
	return basket;
};

/** One line per product and configuration, quantities summed; the user's line wins. */
const combineQuantityOfBasketItems = (
	guestBasketItems: BasketItem[],
	userBasketItems: BasketItem[] | undefined
) =>
	Object.values(
		[...guestBasketItems, ...(userBasketItems ?? [])].reduce(
			(acc: Record<string, BasketItem>, item) => {
				if (!item.ProductId) return acc;
				const key = basketLineKey(item);
				const existing = acc[key];
				return {
					...acc,
					[key]: existing
						? {
								...existing,
								...item,
								Quantity: existing.Quantity + item.Quantity
							}
						: { ...item }
				};
			},
			{}
		)
	);

const getPreferredPricelistSeed = () => {
	// Worked out from the shopper's sales area, not remembered between page
	// loads — see `norceStore.priceListSeed`.
	const seed = useNorceStore().priceListSeed;
	if (seed) return seed;

	// Only reached before the price lists have loaded. The application's own
	// defaults are what Norce would price from anyway.
	const defaultIds = getDefaultPriceListIds();
	return defaultIds != null ? defaultIds : undefined;
};

const isStaleBasketError = (error: unknown) => {
	if (!error || typeof error !== "object") return false;

	const errorRecord = error as Record<string, unknown>;
	const status =
		errorRecord.status ??
		errorRecord.statusCode ??
		(errorRecord.response as Record<string, unknown> | undefined)?.status;

	return status === 400 || status === 404;
};
